import { Button, Card, Carousel, Divider, Icon, Loader, Modal } from '@scuf/common';
import { CompanySelectionModal } from 'components';
import { SsoDTO } from 'models/user/sso';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import 'styles/main.scss';
import { SSO, USER_MANAGER, usuarioAtom } from 'resources';
import { UserCookie, UserPortal } from 'models/user/userAccount';
import useFetch from 'helpers/hooks/useFetch';
import usePreventClickjacking from 'helpers/hooks/usePreventingClickjacking';
import i18n from 'i18n';
import { useRecoilState } from 'recoil';
import useUser from 'helpers/hooks/useUser';
import { downloadPublicFile } from 'resources/api/general_api/PublicFiles';
import { promises } from 'dns';

interface ManualFile {
    id: number;
    displayName: string;
    storedFileName: string;
    section: 'USER';
}

interface HomeSlide {
    id: string;
    image: string;
    title: string;
    subtitle: string;
    visible: boolean;
}

function Home() {
    const { t } = useTranslation(['home', 'homets', 'common', 'certified']);
    const [modalState, setModalState] = useState<'pending' | 'email' | 'company' | undefined>();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('code');
    const [user, setUser] = useState<UserPortal>({} as UserPortal);
    const { user: userDetails, getUser } = useUser();
    const [, getToken, loading] = useFetch<SsoDTO, any>({
        url: process.env.REACT_APP_LOCAL !== undefined ? SSO.generateQAToken : SSO.generateToken,
        method: 'POST'
    });

    const [show, setShow] = useState(false);
    const [fileUrl, setFileUrl] = useState<string>();
    const [, fetchFiles] = useFetch<void, ManualFile[]>({
        url: `${USER_MANAGER.getManualFilesBySection}?section=USER`,
        method: 'GET'
    });

    const [dataLanguage, fetchLanguage, loadingLanguage] = useFetch<{ ssoId: string }>({
        url: SSO.getCultureInfo,
        method: 'GET'
    });

    const slides: HomeSlide[] = [
        {
            id: 'welcome',
            image: require('../../assets/RepairDepot_APP6_1600x600.jpg'),
            title: t('welcome.title'),
            subtitle: t('welcome.info'),
            visible: true
        },
        {
            id: 'repair',
            image: require('../../assets/hero-industrial-printers.jpg'),
            title: t('repair.title'),
            subtitle: t('repair.info'),
            visible: true
        },
        {
            id: 'sales',
            image: require('../../assets/Repair-Depot_APP11_1600x600.jpg'),
            title: t('sales.title'),
            subtitle: t('sales.info'),
            visible: i18n.language !== 'es'
        }
    ];

    useEffect(() => {
        handleToken();
    }, []);

    useEffect(() => {
        if (userDetails && Object.keys(userDetails).length > 0) {
            loadFiles();
            handleLanguage(userDetails.ssoid);
        }
    }, [userDetails]);

    useEffect(() => {
        if (dataLanguage?.data) {
            i18n.changeLanguage(dataLanguage.data);
        }
    }, [dataLanguage]);

    const loadFiles = async (): Promise<void> => {
        try {
            await fetchFiles(undefined, resp => {
                const checkList: ManualFile[] = resp?.data ?? [];
                if (checkList.length > 0) {
                    const foundFile = checkList.find(file => file.displayName === 'mensagemportal');
                    if (foundFile) {
                        const url = `${USER_MANAGER.downloadManualFileById}?id=${foundFile.id}`;
                        setFileUrl(url);
                        setShow(true);
                    }
                }
            });
        } catch (error: any) {
            if (error.status === 401) {
                await getUser();
            } else {
                console.error(t('manual.fetchFilesError'));
            }
        }
    };

    async function handleToken() {
        const data: SsoDTO = {
            code: code,
            redirect_uri: process.env.REACT_APP_SSO_REDIRECT_URI!,
            grant_type: 'authorization_code'
        };

        await getToken(data, async curr => {
            if (curr.message == '1') {
                setUser({
                    ...user,
                    ssoId: curr.data.sub,
                    name: curr.data.given_Name + ' ' + curr.data.family_Name,
                    email: curr.data.email
                });
                setModalState('company');
            } else if (curr.message == '2') {
                setModalState('pending');
            } else {
                await fetch(SSO.generateToken, {
                    method: 'GET',
                    credentials: 'include' // Necessary to ensure cookies are sent and received
                });
                await getUser();
            }
        });
    }

    async function handleLanguage(ssoId: string) {
        await fetchLanguage({ ssoId });
    }

    usePreventClickjacking();

    return (
        <>
            {show && userDetails && Object.keys(userDetails).length > 0 && i18n.languages && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                    onClick={() => setShow(false)}
                >
                    <img
                        style={{ maxWidth: '500px', maxHeight: '500px' }}
                        src={fileUrl}
                        onClick={e => e.stopPropagation()}
                        title="Mensagem Portal"
                    />
                </div>
            )}

            {((loading || loadingLanguage) && <Loader text={t('common:actions.Loading')} />) || (
                <>
                    <CompanySelectionModal
                        modalState={modalState}
                        onChangeModalState={state => setModalState(state as typeof modalState)}
                        user={user}
                    />

                    <Carousel autoPlay transitionTime={5000} height={450}>
                        {slides
                            .filter(slide => slide.visible)
                            .map(slide => (
                                <Carousel.Slide
                                    key={slide.id}
                                    className="img-carousel"
                                    image={slide.image}
                                    title={slide.title}
                                    subtitle={slide.subtitle}
                                />
                            ))}
                    </Carousel>

                    {/* <AboutContainer /> */}
                    <div className="container">
                        <h1>{t('common:services.Services')}</h1>
                        <div className="cards-container-2">
                            <Link to="/info/repair-center">
                                <Card>
                                    <Card.Header title={t('common:services.Repairs')}></Card.Header>
                                    <Card.Content>
                                        <Icon className="largeIcon" name="operations" root="building" exactSize={100} />
                                    </Card.Content>
                                    <Card.Footer>
                                        <Button
                                            type="link"
                                            icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                            iconPosition="right"
                                            content={t('common:actions.LearnMore')}
                                        />
                                    </Card.Footer>
                                </Card>
                            </Link>
                            <Link to="/info/contract">
                                <Card>
                                    <Card.Header title={t('pages:Contracts')}></Card.Header>
                                    <Card.Content>
                                        <Icon className="largeIcon" name="document-certification" root="common" exactSize={100} />
                                    </Card.Content>
                                    <Card.Footer>
                                        <Button
                                            type="link"
                                            icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                            iconPosition="right"
                                            content={t('common:actions.LearnMore')}
                                        />
                                    </Card.Footer>
                                </Card>
                            </Link>
                            {i18n.language !== 'es' && (
                                <Link to="/info/certified">
                                    <Card className="learn-more-card">
                                        <Card.Header title={t('certified:translation.Certified')}></Card.Header>
                                        <Card.Content>
                                            <Icon className="largeIcon" name="user-operations" root="common" exactSize={100} />
                                        </Card.Content>
                                        <Card.Footer>
                                            <Button
                                                type="link"
                                                icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                                iconPosition="right"
                                                content={t('common:actions.LearnMore')}
                                            />
                                        </Card.Footer>
                                    </Card>
                                </Link>
                            )}
                        </div>
                        <div className="cards-container-2">
                            {i18n.language !== 'es' && (
                                <Link to="../spare-parts">
                                    <Card>
                                        <Card.Header title={t('pages:spareParts')}></Card.Header>
                                        <Card.Content>
                                            <Icon className="largeIcon" name="briefcase" root="common" exactSize={100} />
                                        </Card.Content>
                                        <Card.Footer>
                                            <Button
                                                type="link"
                                                icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                                iconPosition="right"
                                                content={t('common:actions.LearnMore')}
                                            />
                                        </Card.Footer>
                                    </Card>
                                </Link>
                            )}
                            {i18n.language !== 'es' && (
                                <Link to="/info/solutions">
                                    <Card>
                                        <Card.Header title={t('common:services.Solutions')}></Card.Header>
                                        <Card.Content>
                                            <Icon
                                                className="largeIcon"
                                                name="checkbox"
                                                root="common"
                                                color="blue"
                                                exactSize={100}
                                            />
                                        </Card.Content>
                                        <Card.Footer>
                                            <Button
                                                type="link"
                                                icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                                iconPosition="right"
                                                content={t('common:actions.LearnMore')}
                                            />
                                        </Card.Footer>
                                    </Card>
                                </Link>
                            )}
                            <Link to="/info/instructions">
                                <Card>
                                    <Card.Header title={t('common:services.Instructions')}></Card.Header>
                                    <Card.Content>
                                        <Icon
                                            className="largeIcon"
                                            name="menu-physical"
                                            root="common"
                                            color="blue"
                                            exactSize={100}
                                        />
                                    </Card.Content>
                                    <Card.Footer>
                                        <Button
                                            type="link"
                                            icon={<Icon className="iconArrow" name="caret-right" root="common" />}
                                            iconPosition="right"
                                            content={t('common:actions.LearnMore')}
                                        />
                                    </Card.Footer>
                                </Card>
                            </Link>
                        </div>
                    </div>
                    {/* <AboutContainer /> */}
                </>
            )}
        </>
    );
}

export default Home;
